/** Enregistrement live : webcam + plateau en un seul flux (pas de fusion après). */
export const RECORD_COMPOSITE_LIVE = true;

/** Caméra paysage préférée (pan). Sur téléphone, repli progressif si refusé. */
export const CAMERA_CONSTRAINTS = {
  video: {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    aspectRatio: { ideal: 16 / 9 },
    frameRate: { ideal: 30, max: 30 },
    facingMode: "user",
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
  },
};

export const CAMERA_CONSTRAINTS_FALLBACK = {
  video: {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    frameRate: { ideal: 30, max: 30 },
    facingMode: "user",
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
  },
};

/** Repli minimal — iPhone / Android souvent plus stricts. */
export const CAMERA_CONSTRAINTS_BASIC = {
  video: {
    facingMode: "user",
  },
  audio: true,
};

export async function getCameraStream() {
  if (!navigator?.mediaDevices?.getUserMedia) {
    throw new Error("getUserMedia indisponible");
  }
  const attempts = [CAMERA_CONSTRAINTS, CAMERA_CONSTRAINTS_FALLBACK, CAMERA_CONSTRAINTS_BASIC];
  let lastErr;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("Accès caméra refusé");
}

/** Vidéo caméra seule (legacy / tests). */
export const RECORDER_BITRATES = {
  videoBitsPerSecond: 450_000,
  audioBitsPerSecond: 48_000,
};

/** Vue plateau 720×1280 (9:16) live. */
export const COMPOSITE_RECORDER_BITRATES = {
  videoBitsPerSecond: 2_800_000,
  audioBitsPerSecond: 96_000,
};

/** MP4 en priorité (iOS Safari) ; WebM en repli. */
const SHARED_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

const COMPOSITE_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
];

export function pickRecorderMimeType({ forComposite = false } = {}) {
  const list = forComposite ? COMPOSITE_MIME_CANDIDATES : SHARED_MIME_CANDIDATES;
  if (typeof MediaRecorder === "undefined") return "video/webm";
  return list.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
}

export function createMediaRecorder(stream, bitrates = RECORDER_BITRATES, options = {}) {
  const mimeType = pickRecorderMimeType(options);
  const withBitrates = { mimeType, ...bitrates };

  try {
    const recorder = new MediaRecorder(stream, withBitrates);
    return { recorder, mimeType: recorder.mimeType || mimeType };
  } catch {
    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      return { recorder, mimeType: recorder.mimeType || mimeType };
    } catch {
      const recorder = new MediaRecorder(stream);
      return { recorder, mimeType: recorder.mimeType || mimeType };
    }
  }
}

const MIX_AUDIO_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm",
];

/** MediaRecorder mix audio — tente MP4 (iOS) puis WebM. */
export function createMixedCompositeRecorder(stream, bitrates = COMPOSITE_RECORDER_BITRATES) {
  if (typeof MediaRecorder === "undefined") {
    return createMediaRecorder(stream, bitrates, { forComposite: true });
  }
  for (const mimeType of MIX_AUDIO_MIME_CANDIDATES) {
    if (!MediaRecorder.isTypeSupported(mimeType)) continue;
    try {
      const recorder = new MediaRecorder(stream, { mimeType, ...bitrates });
      return { recorder, mimeType: recorder.mimeType || mimeType };
    } catch {
      /* essai suivant */
    }
  }
  return createMediaRecorder(stream, bitrates, { forComposite: true });
}
